import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ConfidenceBadge';
import { toast } from 'sonner';
import { Users, ChevronRight, Check } from 'lucide-react';
import { CardsSideNav } from '@/components/CardsSideNav';

import { BLUE, BLUE_D, INK, MUTE, FAINT, LINE, SERIF } from '@/styles/milk';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedWithMePage() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/shared-with-me`);
      setData(res.data.shared_with_me || []);
    } catch {
      toast.error('Failed to load shared cards');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="pagewrap" data-testid="shared-with-me-page" style={{ fontFamily: "'Work Sans', sans-serif" }}>
      <div className="page-head">
        <div>
          <h1 className="pt">Shared with me</h1>
          <p className="ps">Action Cards others have shared with you</p>
        </div>
      </div>
      <div className="ac-layout">
        <aside className="folder-rail"><CardsSideNav /></aside>
        <div className="ac-list-col">
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 rounded-2xl" style={{ background: 'rgba(33,46,64,.06)' }} />)}
        </div>
      ) : data.length === 0 ? (
        <div
          className="rounded-3xl py-14 text-center"
          style={{ background: '#FFFFFF', border: `1px solid ${LINE}`, boxShadow: '0 1px 2px rgba(33,46,64,.04)' }}
          data-testid="shared-with-me-empty"
        >
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(103,150,194,0.10)' }}>
            <Users size={20} style={{ color: BLUE }} />
          </div>
          <p className="text-base font-semibold mb-1.5" style={{ color: INK }}>Nothing shared with you yet</p>
          <p className="text-sm max-w-sm mx-auto" style={{ color: MUTE }}>
            When a teammate shares an Action Card with you — or you open a shared link while logged in — it appears here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.map(({ share, card, tasks, via }) => {
            const pendingCount = tasks.filter(t => t.status !== 'done').length;
            const doneCount = tasks.filter(t => t.status === 'done').length;
            return (
              <div
                key={share.id}
                data-testid="shared-with-me-card"
                className="rounded-2xl p-5 group"
                style={{ background: '#FFFFFF', border: `1px solid ${LINE}`, boxShadow: '0 1px 2px rgba(33,46,64,.04)', transition: 'box-shadow .15s, border-color .15s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 10px 26px rgba(33,46,64,.08)'; e.currentTarget.style.borderColor = 'rgba(103,150,194,.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(33,46,64,.04)'; e.currentTarget.style.borderColor = LINE; }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-lg" style={{ fontFamily: SERIF, color: INK, lineHeight: 1.1 }}>
                        {card.contact_name}
                      </p>
                      <StatusBadge status={card.status} />
                      {via === 'invited' && (
                        <span data-testid="shared-via-invited" className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                          style={{ background: 'rgba(103,150,194,0.12)', color: BLUE_D }}>
                          Shared with you
                        </span>
                      )}
                    </div>
                    <p className="text-xs mb-2" style={{ color: FAINT }}>
                      Shared by <span className="font-semibold" style={{ color: MUTE }}>{share.shared_by_name}</span>
                      {' · '}
                      {new Date(share.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                    {card.summary_bullets?.[0] && (
                      <p className="text-sm line-clamp-2 leading-relaxed mb-2.5" style={{ color: MUTE }}>
                        {card.summary_bullets[0]}
                      </p>
                    )}
                    <div className="flex items-center gap-3 flex-wrap">
                      {pendingCount > 0 && (
                        <span className="text-xs font-medium" style={{ color: BLUE_D }}>
                          {pendingCount} task{pendingCount !== 1 ? 's' : ''} pending
                        </span>
                      )}
                      {doneCount > 0 && (
                        <span className="text-xs flex items-center gap-1" style={{ color: 'hsl(142,52%,34%)' }}>
                          <Check size={11} />{doneCount} done
                        </span>
                      )}
                      {share.permissions?.can_act && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                          style={{ background: 'rgba(103,150,194,0.10)', color: BLUE_D }}>
                          Can take action
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/shared/${share.token}`)}
                    data-testid="shared-with-me-open-link"
                    className="flex items-center gap-1 text-xs font-semibold shrink-0"
                    style={{ color: BLUE_D, background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    Open<ChevronRight size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
